import { lazy, Suspense } from "react";
import { HelmetProvider } from "react-helmet-async";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import ProtectedRoute from "./components/ProtectedRoute";
import { PremiumRoute } from "./components/PremiumRoute";
import { LanguageProvider } from "./i18n/LanguageContext";


import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";


// Lazy load landing page to defer supabase auth-js loading
const Index = lazy(() => import("./pages/Index"));

// Lazy load all pages except the landing page for code splitting
const Auth = lazy(() => import("./pages/Auth"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Tasks = lazy(() => import("./pages/Tasks"));
const Finance = lazy(() => import("./pages/Finance"));
const Menu = lazy(() => import("./pages/Menu"));
const SelfCare = lazy(() => import("./pages/SelfCare"));
const Community = lazy(() => import("./pages/Community"));
const BusinessHub = lazy(() => import("./pages/BusinessHub"));
const AIAssistant = lazy(() => import("./pages/AIAssistant"));
const Notifications = lazy(() => import("./pages/Notifications"));
const AccountSecurity = lazy(() => import("./pages/AccountSecurity"));
const ProfileSettings = lazy(() => import("./pages/ProfileSettings"));
const NotFound = lazy(() => import("./pages/NotFound"));
const ShoppingList = lazy(() => import("./pages/ShoppingList"));
const SharedShoppingList = lazy(() => import("./pages/SharedShoppingList"));
const DailyRoutine = lazy(() => import("./pages/DailyRoutine"));
const Journal = lazy(() => import("./pages/Journal"));
const Movement = lazy(() => import("./pages/Movement"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const OAuthConsent = lazy(() => import("./pages/OAuthConsent"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const PaymentFailed = lazy(() => import("./pages/PaymentFailed"));
const queryClient = new QueryClient();

// Minimal loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

const App = () => (
  <HelmetProvider>
  <QueryClientProvider client={queryClient}>
    <LanguageProvider>
      <TooltipProvider>

        <Toaster />
        <Sonner />

        <BrowserRouter>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-of-service" element={<TermsOfService />} />
              <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
              <Route path="/tasks" element={<ProtectedRoute><Tasks /></ProtectedRoute>} />
              <Route path="/finance" element={<ProtectedRoute><PremiumRoute><Finance /></PremiumRoute></ProtectedRoute>} />
              <Route path="/menu" element={<ProtectedRoute><Menu /></ProtectedRoute>} />
              <Route path="/shopping" element={<ProtectedRoute><ShoppingList /></ProtectedRoute>} />
              <Route path="/shared-list/:token" element={<SharedShoppingList />} />
              <Route path="/self-care" element={<ProtectedRoute><PremiumRoute><SelfCare /></PremiumRoute></ProtectedRoute>} />
              <Route path="/community" element={<ProtectedRoute><Community /></ProtectedRoute>} />
              <Route path="/marketplace" element={<Navigate to="/" replace />} />
              <Route path="/create-shop" element={<Navigate to="/" replace />} />
              <Route path="/shop/:id" element={<Navigate to="/" replace />} />
              <Route path="/product/:id" element={<Navigate to="/" replace />} />
              <Route path="/cart" element={<Navigate to="/" replace />} />
              <Route path="/checkout" element={<Navigate to="/" replace />} />
              <Route path="/admin/products" element={<Navigate to="/" replace />} />
              <Route path="/admin/products/new" element={<Navigate to="/" replace />} />
              <Route path="/admin/products/edit/:id" element={<Navigate to="/" replace />} />
              <Route path="/business" element={<ProtectedRoute><PremiumRoute><BusinessHub /></PremiumRoute></ProtectedRoute>} />
              <Route path="/ai-assistant" element={<ProtectedRoute><PremiumRoute><AIAssistant /></PremiumRoute></ProtectedRoute>} />
              <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
              <Route path="/account-security" element={<ProtectedRoute><AccountSecurity /></ProtectedRoute>} />
              <Route path="/profile" element={<ProtectedRoute><ProfileSettings /></ProtectedRoute>} />
              <Route path="/daily-routine" element={<ProtectedRoute><DailyRoutine /></ProtectedRoute>} />
              <Route path="/journal" element={<ProtectedRoute><PremiumRoute><Journal /></PremiumRoute></ProtectedRoute>} />
              <Route path="/movement" element={<ProtectedRoute><PremiumRoute><Movement /></PremiumRoute></ProtectedRoute>} />
              <Route path="/payment/success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
              <Route path="/payment/failed" element={<ProtectedRoute><PaymentFailed /></ProtectedRoute>} />
              <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </LanguageProvider>
  </QueryClientProvider>
  </HelmetProvider>
);


export default App;
